import { Box } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { PickersDay } from '@mui/x-date-pickers';

export const PickersDayForAvailability = styled(PickersDay)(({ theme }) => 
    ({
        backgroundColor: 'transparent',
        '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.light, 0.5)
        },
        '&.Mui-selected:focus': {
            backgroundColor: alpha(theme.palette.primary.light, 0.5)
        },
        '&.Mui-selected:hover': {
            backgroundColor: alpha(theme.palette.primary.light, 0.5)
        }
    })
);

export const colorForPercentage = (percentage) => {
    if (percentage > 75) return '#70C1B3';
    if (percentage > 50) return '#B3D380';
    if (percentage > 25) return '#ECE08C';
    if (percentage > 0)  return '#FEA180';
    // Legacy backend sentinels: -1 is a blocked interval; -2 is the transparent placeholder used to keep blocked-day rows aligned.
    if (percentage === -1) return '#21759B';
    if (percentage === -2) return 'transparent';
    return '#EB547C';
};

export const timeToMinutes = (time) => {
    if (time === '24:00') {
        return 24 * 60;
    }

    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

const getSegmentForTime = (segments, selectedTime) => {
    if (!Array.isArray(segments) || !selectedTime) {
        return null;
    }

    const selectedMinutes = selectedTime.hour() * 60 + selectedTime.minute();
    return segments.find(segment => {
        const from = timeToMinutes(segment.from);
        const to = timeToMinutes(segment.to);
        return selectedMinutes >= from && selectedMinutes < to;
    }) || null;
};

export const renderWeekPickerDay = (pickersDayProps, withAvailability, availabilityCalendar, shouldDisableDate,
    onMouseOverCallback, onMouseLeaveCallback, options = {}) => {

    const date = pickersDayProps.day;
    const { availabilityDisplayMode = 'segments', selectedTime = null } = options;

    if (!withAvailability || !availabilityCalendar || shouldDisableDate(date)) {
        return <PickersDay {...pickersDayProps} />;
    }

    const formattedDate = date.format("YYYY-MM-DD");
    const availabilityForDay = availabilityCalendar[formattedDate];

    if(!availabilityForDay) {
        return <PickersDay {...pickersDayProps} />;
    }
    
    const percentageForDay = availabilityForDay.percentage;
    const stockLeftForDay = availabilityForDay.stock_left;
    const segments = availabilityForDay.segments;
    const selectedTimeSegment = availabilityDisplayMode === 'selectedTime' ? getSegmentForTime(segments, selectedTime) : null;
    const percentageForDisplay = selectedTimeSegment ? selectedTimeSegment.percentage : percentageForDay;
    const stockLeftForDisplay = selectedTimeSegment ? selectedTimeSegment.stock_left : stockLeftForDay;

    let startWeek = date.startOf('week');
    let endWeek = date.endOf('week');
    let startMonth = date.startOf('month');
    let endMonth = date.endOf('month');
    const prevDayDisabled = shouldDisableDate(date.subtract(1, 'd'));
    const nextDayDisabled = shouldDisableDate(date.add(1, 'd'));

    const isFirstDayOfWeek = date.isSame(startWeek, 'day');
    const isLastDayOfWeek = date.isSame(endWeek, 'day');
    const isFirstDayOfMonth = date.isSame(startMonth, 'day');
    const isLastDayOfMonth = date.isSame(endMonth, 'day');

    const roundLeft = isFirstDayOfWeek || isFirstDayOfMonth || prevDayDisabled;
    const roundRight = isLastDayOfWeek || isLastDayOfMonth || nextDayDisabled;

    const baseBarSx = {
        position: 'absolute',
        width: "100%",
        bottom: "-1px",
        height: "0.75rem",
        display: 'flex',
        overflow: 'hidden',
        ...(roundLeft && { borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }),
        ...(roundRight && { borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }),
    };

    const hasSegments = availabilityDisplayMode === 'segments' && Array.isArray(segments) && segments.length > 1;

    return (
        <Box key={"box_"+formattedDate} sx={{ position: 'relative' }} 
            onMouseEnter={(e) => onMouseOverCallback && onMouseOverCallback(date, e)}
            onMouseLeave={() => onMouseLeaveCallback && onMouseLeaveCallback()}>

            {!pickersDayProps.outsideCurrentMonth && (hasSegments ? (
                <Box sx={baseBarSx}>
                    {segments.map((seg, idx) => {
                        const isFirst = idx === 0;
                        const isLast = idx === segments.length - 1;
                        const segmentColor = colorForPercentage(seg.percentage);
                        return (
                            <Box key={`${formattedDate}_seg_${idx}`} sx={{
                                flexGrow: 1,
                                flexBasis: 0,
                                backgroundColor: segmentColor,
                                ...(isFirst && roundLeft && { borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }),
                                ...(isLast && roundRight && { borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }),
                            }} />
                        );
                    })}
                </Box>
            ) : (
                <Box sx={{
                    ...baseBarSx,
                    backgroundColor: colorForPercentage(percentageForDisplay),
                    color: '#FDFDFD',
                    fontSize: "0.50rem",
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    { stockLeftForDisplay }
                </Box>
            ))}

            <PickersDayForAvailability {...pickersDayProps} />
        </Box>
    );
};
